import { format } from "date-fns";
import {
  Calendar, Clock, MapPin, User, BookOpen,
  CheckCircle2, Circle, Lock, LinkIcon, Image,
} from "lucide-react";
import { ADJUSTMENT_MAP } from "@/lib/nccd-adjustments";
import { useRegion } from "@/contexts/RegionContext";

// Local interface — mirrors the ListRecordsResponseItem shape plus
// enteredIntoSentral which the server includes via spread but isn't in the
// strict OpenAPI schema (hence the separate field).
export interface RecordForPreview {
  id: number;
  type: string;
  date: Date;
  period?: string | null;
  teacherName?: string | null;
  location?: string | null;
  subject?: string | null;
  isConfidential: boolean;
  linkedToActivity: boolean;
  shorthandNotes?: string | null;
  description?: string | null;
  status: string;
  fields?: Record<string, unknown> | null;
}

interface RecordPreviewPanelProps {
  record: RecordForPreview;
  enteredIntoSentral: boolean;
  studentNames: string[];
  className?: string | null;
  courseName?: string | null;
}

// ─── Small layout helpers ──────────────────────────────────────────────────────

function MetaItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
}) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
        {label}
      </div>
      <div className="flex items-center gap-1 text-xs text-gray-700">
        <Icon className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}

function PreviewSection({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function Pill({
  children,
  color,
}: {
  children: React.ReactNode;
  color: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}
    >
      {children}
    </span>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

export function RecordPreviewPanel({
  record,
  enteredIntoSentral,
  studentNames,
  className,
  courseName,
}: RecordPreviewPanelProps) {
  const { terms } = useRegion();

  const fields = record.fields ?? {};
  const adjustmentIds = Array.isArray(fields.adjustments)
    ? (fields.adjustments as string[])
    : [];
  const photoObjectPaths = Array.isArray(fields.photoUrls)
    ? (fields.photoUrls as string[])
    : [];

  const resolvedAdjustments = adjustmentIds
    .map((id) => ADJUSTMENT_MAP.get(id))
    .filter(Boolean) as NonNullable<ReturnType<typeof ADJUSTMENT_MAP.get>>[];

  const statusLabel: Record<string, string> = {
    active: "Active",
    completed: "Completed",
    further_action_required: "Further action required",
  };

  return (
    <div className="border-t border-gray-100 bg-gray-50/70 px-4 pt-3 pb-4 space-y-4">

      {/* ── Record meta ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <MetaItem
          icon={Calendar}
          label="Date"
          value={format(new Date(record.date), "d MMM yyyy")}
        />
        {record.period && (
          <MetaItem icon={Clock} label="Period" value={record.period} />
        )}
        {record.location && (
          <MetaItem icon={MapPin} label="Location" value={record.location} />
        )}
        {record.teacherName && (
          <MetaItem icon={User} label="Teacher" value={record.teacherName} />
        )}
        {courseName && (
          <MetaItem icon={BookOpen} label="Subject" value={courseName} />
        )}
        {!courseName && className && (
          <MetaItem icon={BookOpen} label="Class" value={className} />
        )}
        {record.subject && !courseName && (
          <MetaItem icon={BookOpen} label="Subject" value={record.subject} />
        )}
      </div>

      {/* ── Students ────────────────────────────────────────────────────── */}
      {studentNames.length > 0 && (
        <PreviewSection label="Students">
          <div className="flex flex-wrap gap-1.5">
            {studentNames.map((name, i) => (
              <Pill key={i} color="border-blue-100 bg-blue-50 text-blue-800">
                {name}
              </Pill>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* ── Professional note ───────────────────────────────────────────── */}
      {record.description && (
        <PreviewSection label="Professional note">
          <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
            {record.description}
          </p>
        </PreviewSection>
      )}

      {/* ── Original / shorthand note ────────────────────────────────────  */}
      {record.shorthandNotes && (
        <PreviewSection label="Original note">
          <p className="text-sm text-gray-500 whitespace-pre-wrap leading-relaxed italic">
            {record.shorthandNotes}
          </p>
        </PreviewSection>
      )}

      {/* ── Adjustments / supports ──────────────────────────────────────── */}
      {resolvedAdjustments.length > 0 && (
        <PreviewSection label={`${terms.adjustmentType} adjustments`}>
          <div className="flex flex-wrap gap-1.5">
            {resolvedAdjustments.map((adj) => (
              <Pill
                key={adj.id}
                color="border-purple-100 bg-purple-50 text-purple-800"
              >
                <span title={adj.description}>{adj.label}</span>
              </Pill>
            ))}
          </div>
        </PreviewSection>
      )}

      {/* ── Photo attachments ────────────────────────────────────────────── */}
      {photoObjectPaths.length > 0 && (
        <PreviewSection label="Photos">
          <div className="flex flex-wrap gap-2">
            {photoObjectPaths.map((objectPath, i) => (
              <img
                key={i}
                src={`/api/storage${objectPath}`}
                alt={`Attachment ${i + 1}`}
                className="h-20 w-20 object-cover rounded-lg border border-gray-200 bg-gray-100"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            ))}
            {photoObjectPaths.length === 0 && (
              <span className="flex items-center gap-1.5 text-xs text-gray-400">
                <Image className="h-4 w-4" />
                No photos
              </span>
            )}
          </div>
        </PreviewSection>
      )}

      {/* ── Status / metadata ────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        {enteredIntoSentral ? (
          <Pill color="border-green-200 bg-green-50 text-green-700">
            <CheckCircle2 className="h-3 w-3" />
            Uploaded to system
          </Pill>
        ) : (
          <Pill color="border-gray-200 bg-gray-100 text-gray-500">
            <Circle className="h-3 w-3" />
            Not yet uploaded
          </Pill>
        )}
        {record.isConfidential && (
          <Pill color="border-amber-200 bg-amber-50 text-amber-700">
            <Lock className="h-3 w-3" />
            Confidential
          </Pill>
        )}
        {record.linkedToActivity && (
          <Pill color="border-sky-200 bg-sky-50 text-sky-700">
            <LinkIcon className="h-3 w-3" />
            Linked to activity
          </Pill>
        )}
        {record.status !== "active" && (
          <Pill color="border-gray-200 bg-gray-100 text-gray-600">
            {statusLabel[record.status] ?? record.status}
          </Pill>
        )}
      </div>
    </div>
  );
}
